import React from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { BarChartOutlined } from "@ant-design/icons";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import MainCard from "components/MainCard";
import { IDepartmentDistributionItem } from "../api/types";

interface DepartmentBarChartProps {
  data?: IDepartmentDistributionItem[];
  isLoading: boolean;
}

const BAR_COLORS = [
  "#2f54eb",
  "#1890ff",
  "#13c2c2",
  "#52c41a",
  "#faad14",
  "#fa8c16",
  "#f5222d",
  "#722ed1",
  "#eb2f96",
  "#fa541c",
];

export default function DepartmentBarChart({
  data,
  isLoading,
}: DepartmentBarChartProps) {
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.slice(0, 10).map((d) => ({
      name: d.departmentName || "Unknown",
      institute: d.instituteName || "",
      visits: d.visitCount,
    }));
  }, [data]);

  const hasData = chartData.length > 0;

  return (
    <MainCard sx={{ mt: 1 }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            component="h3"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
            }}
          >
            <BarChartOutlined style={{ fontSize: 22, color: "#1890ff" }} />
            Top Departments Visiting Library
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ height: { xs: 320, sm: 380, md: 420 }, width: "100%" }}>
        {isLoading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : !hasData ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              No department data for this period.
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 10, right: 20, left: -10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis
                type="category"
                dataKey="name"
                width={120}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(
                  val: number | string | undefined,
                  _name: unknown,
                  item: { payload?: { institute?: string } }
                ) => [
                  `${val} Visits ${
                    item?.payload?.institute ? `(${item.payload.institute})` : ""
                  }`,
                  "Visits",
                ]}
              />
              <Bar dataKey="visits" radius={[0, 4, 4, 0]}>
                {chartData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={BAR_COLORS[index % BAR_COLORS.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </MainCard>
  );
}

